/**
 * Enhanced Supabase Stub with Proper Promise Support
 * This version returns actual Promises for await compatibility
 */

// Debug flag - set to false to hide console.log statements
const DEBUG = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG !== 'false';

// Supabase stub v2 initialized

// Use relative path for API calls in browser environment
// The webpack dev server proxy forwards /api/data to nexus
const API_BASE_URL = '';

// Helper to make API calls with proper error handling
async function apiCall(action, table, options = {}) {
  try {
    const endpoint = '/api/data';
    const payload = {
      action,
      table,
      ...options
    };
    
    // Debug: API call
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    // Debug: API result
    return result;
  } catch (error) {
    console.error(`[API Error] ${table}:`, error.message);
    return { data: null, error: error.message };
  }
}

// Actual Promise-based query builders
class SelectQuery {
  constructor(table) {
    this.table = table;
    this.filters = {};
    this.columns = '*';
    this.limit = 10000;  // Increased from 1000 to handle larger datasets like member_contributions
    this.offset = 0;
    this.singleRow = false;
    this.singleMaybe = false;
  }

  eq(column, value) {
    this.filters[column] = value;
    return this;
  }

  in(column, values) {
    this.filters[column] = values;
    return this;
  }

  or(column, value) {
    this.filters[column] = value;
    return this;
  }

  range(start, end) {
    this.offset = start;
    this.limit = (end - start) + 1;
    return this;
  }

  order(column, options = {}) {
    // Store order for client-side sorting
    this.orderColumn = column;
    this.orderAscending = !options.ascending || options.ascending !== false;
    return this;
  }

  single() {
    this.singleRow = true;
    return this;
  }

  maybeSingle() {
    this.singleMaybe = true;
    return this;
  }

  // Return actual Promise
  then(resolve, reject) {
    return this._execute().then(resolve, reject);
  }

  catch(reject) {
    return this._execute().catch(reject);
  }

  [Symbol.toStringTag] = 'Promise';

  async _execute() {
    try {
      const result = await apiCall('get', this.table, {
        filters: this.filters,
        limit: this.limit,
        offset: this.offset
      });

      let data = result.data || [];
      if (!Array.isArray(data)) {
        data = data ? [data] : [];
      }

      // Client-side sorting if order was specified
      if (this.orderColumn && data.length > 0) {
        data.sort((a, b) => {
          const aVal = a[this.orderColumn];
          const bVal = b[this.orderColumn];
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return this.orderAscending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return this.orderAscending ? aVal - bVal : bVal - aVal;
          }
          
          return 0;
        });
      }

      // Parse PostgreSQL arrays back to JavaScript arrays
      data = data.map(row => {
        if (row.aliases && typeof row.aliases === 'string' && row.aliases.startsWith('{')) {
          // Convert PostgreSQL array format {item1,item2} to JavaScript array [item1, item2]
          const arrayStr = row.aliases.slice(1, -1); // Remove { and }
          row.aliases = arrayStr.length > 0 ? arrayStr.split(',') : [];
        }
        return row;
      });

      if (this.singleRow || this.singleMaybe) {
        return { 
          data: data.length > 0 ? data[0] : null,
          error: result.error || null
        };
      }

      return {
        data: data,
        error: result.error || null
      };
    } catch (error) {
      return { data: null, error: error.message };
    }
  }
}

class InsertQuery {
  constructor(table, records) {
    this.table = table;
    this.records = Array.isArray(records) ? records : [records];
  }

  select(cols) {
    return this;
  }

  then(resolve, reject) {
    return this._execute().then(resolve, reject);
  }

  catch(reject) {
    return this._execute().catch(reject);
  }

  [Symbol.toStringTag] = 'Promise';

  async _execute() {
    try {
      const results = [];
      for (const record of this.records) {
        const result = await apiCall('insert', this.table, { data: record });
        if (result.data) {
          results.push(result.data);
        }
      }
      return { 
        data: results.length === 1 ? results[0] : results,
        error: null
      };
    } catch (error) {
      return { data: null, error: error.message };
    }
  }
}

class UpdateQuery {
  constructor(table, data) {
    this.table = table;
    this.data = data;
    this.filters = {};
  }

  eq(column, value) {
    this.filters[column] = value;
    return this;
  }

  in(column, values) {
    this.filters[column] = values;
    return this;
  }

  select(cols) {
    return this;
  }

  then(resolve, reject) {
    return this._execute().then(resolve, reject);
  }

  catch(reject) {
    return this._execute().catch(reject);
  }

  [Symbol.toStringTag] = 'Promise';

  async _execute() {
    try {
      if (this.filters.id) {
        const result = await apiCall('update', this.table, {
          id: this.filters.id,
          data: this.data
        });
        // Parse PostgreSQL arrays back to JavaScript arrays
        if (result.data) {
          const data = result.data;
          if (data.aliases && typeof data.aliases === 'string' && data.aliases.startsWith('{')) {
            const arrayStr = data.aliases.slice(1, -1); // Remove { and }
            data.aliases = arrayStr.length > 0 ? arrayStr.split(',') : [];
          }
        }
        return { data: result.data, error: result.error || null };
      }
      return { data: null, error: 'ID filter required' };
    } catch (error) {
      return { data: null, error: error.message };
    }
  }
}

class DeleteQuery {
  constructor(table) {
    this.table = table;
    this.filters = {};
  }

  eq(column, value) {
    this.filters[column] = value;
    return this;
  }

  then(resolve, reject) {
    return this._execute().then(resolve, reject);
  }

  catch(reject) {
    return this._execute().catch(reject);
  }

  [Symbol.toStringTag] = 'Promise';

  async _execute() {
    try {
      if (this.filters.id) {
        const result = await apiCall('delete', this.table, { id: this.filters.id });
        return { data: result.data, error: result.error || null };
      }
      return { data: null, error: 'ID filter required' };
    } catch (error) {
      return { data: null, error: error.message };
    }
  }
}

// Create supabase object
// Track auth state for the stub - restore from localStorage if available
let authSession = (() => {
  if (typeof window === 'undefined') return null; // Node environment
  
  const stored = localStorage.getItem('hot_auth_session');
  if (stored && stored !== 'null') {
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  }
  return null; // Start logged out by default
})();

let authCallbacks = [];

// Helper to save auth state to localStorage
function saveAuthState(session) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('hot_auth_session', JSON.stringify(session || null));
  }
}

const supabase = {
  from: (table) => ({
    select(cols = '*') {
      return new SelectQuery(table);
    },
    insert(records) {
      return new InsertQuery(table, records);
    },
    update(data) {
      const q = new UpdateQuery(table, data);
      return {
        eq: (col, val) => { q.eq(col, val); return this; },
        in: (col, vals) => { q.in(col, vals); return this; },
        select: (cols) => { q.select(cols); return this; },
        then: (resolve, reject) => q.then(resolve, reject),
        catch: (reject) => q.catch(reject),
        [Symbol.toStringTag]: 'Promise'
      };
    },
    delete() {
      const q = new DeleteQuery(table);
      return {
        eq: (col, val) => { q.eq(col, val); return this; },
        then: (resolve, reject) => q.then(resolve, reject),
        catch: (reject) => q.catch(reject),
        [Symbol.toStringTag]: 'Promise'
      };
    },
    upsert(records, opts) {
      return new InsertQuery(table, records);
    }
  }),

  auth: {
    signInWithPassword: async ({ email, password }) => {
      authSession = { user: { id: 'e73e5277-b267-4e5b-ae2e-b70519b514b6', email }, session: { access_token: 'token' } };
      saveAuthState(authSession);
      authCallbacks.forEach(cb => cb('SIGNED_IN', { user: authSession.user }));
      return {
        data: { user: authSession.user, session: { access_token: 'token' } },
        error: null
      };
    },
    signOut: async () => {
      authSession = null;
      saveAuthState(null);
      authCallbacks.forEach(cb => cb('SIGNED_OUT', null));
      return { error: null };
    },
    getSession: async () => ({
      data: { session: authSession ? { user: authSession.user } : null },
      error: null
    }),
    onAuthStateChange: (callback) => {
      authCallbacks.push(callback);
      // Call with current state
      if (authSession) {
        callback('SIGNED_IN', { user: authSession.user });
      } else {
        callback('SIGNED_OUT', null);
      }
      return { data: { subscription: { unsubscribe: () => { 
        authCallbacks = authCallbacks.filter(cb => cb !== callback);
      }} } };
    }
  },

  storage: {
    from: (bucket) => ({
      upload: async (path, file) => ({ data: { path }, error: null }),
      getPublicUrl: (path) => ({ data: { publicUrl: path } }),
      remove: async (paths) => ({ data: paths, error: null })
    })
  }
};

// Add createClient method for compatibility with supabaseClient.js
supabase.createClient = (url, key) => {
  // Supabase client created
  return supabase; // Return the stub itself
};

// Ready - API calls will use /api/data endpoint

// Export for Node environments, assign to window for browsers
if (typeof module !== 'undefined' && module.exports) {
  module.exports = supabase;
}
if (typeof window !== 'undefined') {
  window.supabase = supabase;
}
